import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { TranslocoDirective } from '@jsverse/transloco';
import { MatButton } from '@angular/material/button';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MindDatetimeComponent } from '../mind-date-time/mind-datetime.component';
import { MatError, MatFormField, MatLabel } from '@angular/material/input';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { SessionService } from '../../../features/session/session.service';
import { finalize } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { downloadFile } from '../../utils/download-file';
import type { HttpResponse } from '@angular/common/http';
import type { SessionRecordingData } from '../../../features/session/model/session-recording-data';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { parseFilenameFromResponse } from '../../utils/parse-filename-from-response';
import { ValidatorErrorPipe } from '../../pipes/validator-error';
import { SnackbarService } from '../../../core/snackbar.service';
import { RecordingService } from '../../../features/session/recording.service';

@Component({
  selector: 'app-recording-export-dialog',
  imports: [
    MatDialogTitle,
    TranslocoDirective,
    MatButton,
    MatDialogActions,
    FormsModule,
    ReactiveFormsModule,
    MatDialogContent,
    MindDatetimeComponent,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    MatIcon,
    MatProgressSpinner,
    MatError,
    ValidatorErrorPipe,
  ],
  templateUrl: './recording-export-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SessionService, RecordingService],
})
export class RecordingExportDialogComponent {
  private readonly recordingService = inject(RecordingService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogRef = inject<MatDialogRef<RecordingExportDialogComponent, boolean>>(MatDialogRef);
  protected readonly data = inject<SessionRecordingData>(MAT_DIALOG_DATA);

  protected readonly startedAtControl = new FormControl(
    { value: new Date(this.data.startedAt), disabled: true },
    { validators: [Validators.required], nonNullable: true },
  );

  protected readonly participantControl = new FormControl<string | null>(null, {
    validators: [Validators.required],
  });

  protected readonly form = new FormGroup({
    startedAt: this.startedAtControl,
    participant: this.participantControl,
  });

  protected readonly exportInProgress = signal(false);

  protected save(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid || this.participantControl.value === null) {
      return;
    }

    this.exportInProgress.set(true);

    this.recordingService
      .export(this.data.id, this.participantControl.value)
      .pipe(
        finalize(() => this.exportInProgress.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(response => this.handleExportResponse(response));
  }

  protected cancel(): void {
    this.dialogRef.close();
  }

  private handleExportResponse(response: HttpResponse<Blob>): void {
    const file = response.body;
    const filename = parseFilenameFromResponse(response);

    if (file === null || filename === null) {
      this.snackbarService.showError('error.exportFileCorrupted');
      return;
    }

    downloadFile(file, filename);
    this.dialogRef.close(true);
  }
}
